import React from 'react';
import { motion } from 'framer-motion';
import { testimonials, fadeInUp, staggerContainer } from '../data/constants';

const Testimonials: React.FC = () => {
    const renderStars = (rating: number) => {
        return Array.from({ length: 5 }, (_, index) => (
            <svg
                key={index}
                className={`w-5 h-5 ${index < rating ? 'text-brand-gold' : 'text-gray-300'
                    }`}
                fill="currentColor"
                viewBox="0 0 20 20"
            >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
        ));
    };

    return (
        <section id="testimonials" className="py-20 bg-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    variants={staggerContainer}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    className="text-center mb-16"
                >
                    <motion.h2
                        variants={fadeInUp}
                        className="font-cinzel text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
                    >
                        What Our <span className="text-brand-dark-red">Students Say</span>
                    </motion.h2>
                    <motion.p
                        variants={fadeInUp}
                        className="font-poppins text-xl text-gray-600 max-w-3xl mx-auto"
                    >
                        Join thousands of happy students who have transformed their lives through music, yoga, and dance
                    </motion.p>
                </motion.div>

                <motion.div
                    variants={staggerContainer}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                >
                    {testimonials.map((testimonial) => (
                        <motion.div
                            key={testimonial.id}
                            className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-all duration-300"
                        >
                            {/* Decorative background element */}
                            <div className="absolute top-0 right-0 w-20 h-20 bg-brand-dark-red/10 rounded-full transform translate-x-8 -translate-y-8"></div>

                            {/* Quote icon */}
                            <div className="absolute top-6 left-6 w-8 h-8 text-brand-gold opacity-50">
                                <svg fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z" />
                                </svg>
                            </div>

                            {/* Rating */}
                            <div className="flex items-center mb-4 relative z-10">
                                <div className="flex space-x-1">
                                    {renderStars(testimonial.rating)}
                                </div>
                            </div>

                            {/* Quote */}
                            <blockquote className="font-poppins text-gray-700 text-lg leading-relaxed mb-6 relative z-10">
                                "{testimonial.quote}"
                            </blockquote>

                            {/* Student info */}
                            <div className="flex items-center relative z-10">
                                <div className="w-12 h-12 bg-brand-dark-red rounded-full flex items-center justify-center text-white font-poppins font-bold text-lg">
                                    {testimonial.name.charAt(0)}
                                </div>
                                <div className="ml-4">
                                    <div className="font-poppins font-semibold text-gray-900">
                                        {testimonial.name}, {testimonial.age}
                                    </div>
                                    <div className="font-poppins text-brand-dark-red text-sm">
                                        {testimonial.course}
                                    </div>
                                    {testimonial.location && (
                                        <div className="font-poppins text-gray-500 text-xs">
                                            {testimonial.location}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Decorative bottom border */}
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-brand-dark-red"></div>
                        </motion.div>
                    ))}
                </motion.div>

                {/* Call to action */}
                <motion.div
                    variants={fadeInUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="text-center mt-16"
                >
                    <div className="bg-brand-dark-red/5 rounded-3xl p-8 max-w-4xl mx-auto">
                        <h3 className="font-cinzel text-3xl font-bold text-gray-900 mb-4">
                            Ready to Write Your Success Story?
                        </h3>
                        <p className="font-poppins text-xl text-gray-600 mb-8">
                            Join our community of passionate learners and discover your potential
                        </p>

                        {/* Stats */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                            <div className="text-center">
                                <div className="text-3xl font-bold text-brand-dark-red mb-2">4.9/5</div>
                                <div className="font-poppins text-gray-600">Average Rating</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-bold text-brand-gold mb-2">500+</div>
                                <div className="font-poppins text-gray-600">Happy Students</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-bold text-brand-dark-red mb-2">95%</div>
                                <div className="font-poppins text-gray-600">Satisfaction Rate</div>
                            </div>
                        </div>

                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                                const element = document.getElementById('pricing');
                                element?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            className="bg-brand-gold text-gray-900 px-6 py-3 rounded-lg font-poppins font-semibold text-base shadow-lg hover:bg-yellow-500 hover:shadow-xl transition-all duration-300"
                        >
                            Start Your Journey Today
                        </motion.button>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Testimonials; 
